import { Dispatch, SetStateAction } from "react";
import { Col, Form, Row } from "react-bootstrap";

/**
 * Props for the internal CheckedGroup component.
 */
export interface CheckedGroupProps {
  id: string;
  labels: string[];
  groupLabel: string;
  labelWidth: number;
  maxWidthCols: number;
  offsetCols: number;
  checked: boolean[];
  setChecked: Dispatch<boolean[]>;
  disabled?: boolean;
}

/**
 * Internal component for rendering a group of related checkboxes with a "Select All" option.
 *
 * @param props - Component props
 * @param props.id - Unique identifier for the checkbox group
 * @param props.checked - Array of checked states for each checkbox
 * @param props.disabled - Whether all checkboxes are disabled
 * @param props.groupLabel - Overall label for the group
 * @param props.labelWidth - Width of the label column in Bootstrap grid units
 * @param props.labels - Array of labels for individual checkboxes
 * @param props.maxWidthCols - Maximum width in Bootstrap grid columns
 * @param props.offsetCols - Number of columns to offset the group
 * @param props.setChecked - Callback function to update all checked states
 * @returns A React functional component rendering a Form.Group with multiple checkboxes
 */
const CheckedGroup = ({
  id,
  groupLabel,
  labels,
  labelWidth,
  maxWidthCols,
  offsetCols,
  checked,
  setChecked,
  disabled,
}: CheckedGroupProps): React.ReactElement => {
  return (
    <Form.Group
      controlId={id}
      as={Row}
    >
      <Form.Label
        column
        md={labelWidth}
        offset={offsetCols}
      >
        {groupLabel}
      </Form.Label>
      <Col md={maxWidthCols - labelWidth - offsetCols}>
        <Form.Check
          id={`${id}-select-all`}
          className="mb-1"
          label="Select All"
          type="checkbox"
          checked={labels.length === checked.filter((c) => c).length}
          onChange={(e) => {
            setChecked(labels.map(() => e.target.checked));
          }}
          disabled={disabled}
        />
        {labels.map((label, index) => (
          <Form.Check
            key={index}
            id={`${id}-${index}`}
            type="checkbox"
            name={`${id}-${index}`}
            label={label}
            checked={checked[index]}
            onChange={(e) => {
              const newChecked = [...checked];
              newChecked[index] = e.target.checked;
              setChecked(newChecked);
            }}
            disabled={disabled}
          />
        ))}
      </Col>
    </Form.Group>
  );
};

CheckedGroup.displayName = "CheckedGroup";

/**
 * Props interface for the Control213 component.
 */
export interface Control213Props {
  id: string;
  label: string | string[];
  groupLabel?: string;
  labelWidth?: number;
  maxWidthCols?: number;
  offsetCols?: number;
  checked: boolean | boolean[];
  setChecked?: Dispatch<boolean> | Dispatch<boolean[]>;
  disabled?: boolean;
}

/**
 * A checkbox control that supports both single checkboxes and grouped checkbox collections with flexible labeling options.
 *
 * @param props - Component props.
 * @param props.id - Unique control id used for DOM attributes
 * @param props.label - Control label string or array of labels for grouped mode
 * @param props.groupLabel - Optional label shown above a grouped set of checkboxes
 * @param props.labelWidth - Column width for the label (Bootstrap grid units)
 * @param props.maxWidthCols - Total number of columns available for the control
 * @param props.offsetCols - Column offset to apply to the label column
 * @param props.checked - Boolean or array representing the checked state(s)
 * @param props.setChecked - Callback to update the checked state (single or array)
 * @param props.disabled - When true, disables the control(s)
 */
export const Control213 = ({
  id,
  label,
  groupLabel,
  labelWidth = 2,
  maxWidthCols = 12,
  offsetCols,
  checked,
  setChecked,
  disabled,
}: Control213Props): React.ReactElement => {
  const sensibleCheck = new Set([Array.isArray(checked), Array.isArray(label)]);

  return sensibleCheck.size > 1 ? (
    <>Mismatch in labels/checks</>
  ) : (
    <>
      {typeof label === "string" && typeof checked === "boolean" ? (
        <Form.Group
          controlId={`${id}`}
          as={Row}
          className="mb-1"
        >
          <Col
            md={{ span: maxWidthCols - labelWidth - (offsetCols ?? 0), offset: offsetCols }}
            className="d-flex align-items-center"
          >
            <Form.Check
              type="checkbox"
              name={id}
              label={label}
              checked={checked}
              onChange={(e) => setChecked && (setChecked as Dispatch<boolean>)(e.target.checked)}
              disabled={disabled}
            />
          </Col>
        </Form.Group>
      ) : (
        <CheckedGroup
          id={id}
          groupLabel={groupLabel || ""}
          labels={label as string[]}
          labelWidth={labelWidth}
          maxWidthCols={maxWidthCols}
          offsetCols={offsetCols ?? 0}
          checked={checked as boolean[]}
          setChecked={setChecked as Dispatch<SetStateAction<boolean[]>>}
          disabled={disabled}
        />
      )}
    </>
  );
};

Control213.displayName = "Control213";
